import { Button, Card, formatPhoneNumber, FormattedMessageDiv, FormInput, FormWrapper, LoadingAnimation, Modal, useWizardContext } from '@odmonorepo/core-components';
import { formatDateInput, formatToISODate, validateDateComplete, validateDateFormat, validateNotFuture } from '@odmonorepo/odfunctions';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'merchant-api';
import type { Application } from 'merchant-api/types';
import { unifiedApiClient, useApplication } from 'merchant-api/unified';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';
import { ProveMobileAuthContent } from '../../Login/ProveMobileAuth/ProveMobileAuth';
import { trackProveAuth, trackProveStep, type ProveAuthAction, type ProveAuthMethod, type ProveFlowType, type ProveStepAction } from '../../Prove/proveAnalytics';
import {
  getProveFallbackError,
  useProveAuthenticator,
  type ProveAuthenticatorBuilderFactory,
  type ProveFallbackMeta,
} from '../../Prove/useProveAuthenticator';
import { hasProvePrefillConsent, useProveStepDecision } from '../helpers';
import { getProveStepDefaults, getProveStepFieldVisibility, type ProveStepFieldVisibility } from '../proveStepFields';

/**
 * PROVE_STEP (XF-10207): Prove phone verification as the first app-intake step, decoupled from
 * auth. Every entry point already granted the app-token session, so start/complete only
 * authenticate the Prove session — no cookies change during the run.
 *
 *  form     → collect only what the application is missing (+ DOB always, phone always editable)
 *  verify   → prove/start, then the Prove SDK in-step (instant link on desktop, OTP on mobile)
 *  fallback → Prove failed / declined: continue to manual entry (already authed — no OTP detour)
 *
 * Run-once: the step self-skips when provePrefillConsent is already on the application or the
 * applicant is not in the M3 test bucket. Prove state (authToken / key / phoneMasked) lives in
 * component state only — a hard reload discards the in-flight session instead of resuming it,
 * which is what keeps the gate honest (there is no server-side session to resume).
 */

type ProveStartRequest = {
  appId: string;
  businessName: string;
  dateOfBirth: string;
  email?: string;
  flowType: ProveFlowType;
  phoneNumber: string;
  provePrefillConsent: string;
};

type ProveStartResponse = {
  appId?: string;
  authToken: string;
  flowType: ProveFlowType;
  key: string;
  phoneMasked?: string;
};

type ProveCompleteResponse = {
  appId: string;
  application: Application;
};

class ProveStepApiError extends Error {
  status?: number;
  data?: unknown;

  constructor(status?: number, data?: unknown) {
    super('Prove request failed.');
    this.status = status;
    this.data = data;
  }
}

type ProveStepFormValues = {
  businessName?: string;
  dateOfBirth: string;
  email?: string;
  phoneNumber: string;
};

type Phase =
  | { name: 'form' }
  | { name: 'verify'; session: ProveStartResponse; phoneNumber: string }
  | { name: 'fallback'; error?: string };

const startProve = async (body: ProveStartRequest) => {
  const result = await unifiedApiClient.POST('/client/prove/start' as never, { body } as never);

  if (!result.response.ok) throw new ProveStepApiError(result.response.status, result.error);

  return result.data as unknown as ProveStartResponse;
};

const completeProve = async (body: { appId: string; key: string }) => {
  const result = await unifiedApiClient.POST('/client/prove/complete' as never, { body } as never);

  if (!result.response.ok) throw new ProveStepApiError(result.response.status, result.error);

  return result.data as unknown as ProveCompleteResponse;
};

const getProveFlowType = (): ProveFlowType =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';

const digitsOnly = (value?: string) => (value ?? '').replace(/\D/g, '');

const buildSchema = (visibility: ProveStepFieldVisibility) =>
  yup.object({
    ...(visibility.businessName ? { businessName: yup.string().trim().required('formInput.party.businesses.legalName.error') } : {}),
    ...(visibility.email ? { email: yup.string().required('formInput.email.error').email('formInput.email.invalid') } : {}),
    dateOfBirth: yup
      .string()
      .required('formInput.dateOfBirth.error')
      .test('must be complete date', 'formInput.dateOfBirth.error', validateDateComplete)
      .test('must be valid date', 'formInput.dateOfBirth.error.invalidFormat', validateDateFormat)
      .test('must be in past', 'formInput.dateOfBirth.error.futureDate', validateNotFuture),
    phoneNumber: yup
      .string()
      .required('formInput.homePhone.error')
      .test('is-10-digits', 'formInput.homePhone.invalid', (value) => digitsOnly(value).length === 10),
  });

const noop = () => undefined;

export interface ProveStepProps {
  /** Test seam (Storybook / Playwright): replaces the Prove SDK builder. Production never passes it. */
  createAuthenticatorBuilder?: ProveAuthenticatorBuilderFactory;
}

export const ProveStep = ({ createAuthenticatorBuilder }: ProveStepProps = {}) => {
  const { applicationData } = useApplication();
  const { nextStep, skipStep } = useWizardContext();
  const { decision, isLoading: decisionLoading, proveStepEligible } = useProveStepDecision(applicationData);
  const advancedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>({ name: 'form' });
  const [showHowWeVerify, setShowHowWeVerify] = useState(false);
  const flowType = useMemo(getProveFlowType, []);

  const channelCode = applicationData?.metadata?.channelCode;
  const bucket = decision.inProveStep ? 'test' : decision.isM3Rule ? 'control' : undefined;
  const track = useCallback((action: ProveStepAction, properties?: Record<string, unknown>) => {
    trackProveStep({ action, bucket, channelCode, flowType, properties });
  }, [bucket, channelCode, flowType]);
  const trackAuth = useCallback((action: ProveAuthAction, properties: Record<string, unknown> = {}, method?: ProveAuthMethod) => {
    trackProveAuth({ action, associate: false, flowType, method, properties: { ...properties, surface: 'PROVE_STEP' } });
  }, [flowType]);

  // Advance exactly once, whichever path triggers it (self-skip, manual entry, verified, declined).
  const advance = useCallback(() => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    nextStep();
  }, [nextStep]);

  // Self-skip (mirrors BusinessSelectStep): once the application and the bucket are known, leave
  // immediately when consent is already set (run-once) or the applicant is not in the test bucket.
  // Direct-apply is intercepted at /apply/landing, but MLP has no pre-wizard gate and always reaches
  // the wizard, so the step must be able to skip itself from inside. Spinner meanwhile, never nothing.
  const loaded = !!applicationData && !decisionLoading;
  const shouldSkip = loaded && phase.name === 'form' && (hasProvePrefillConsent(applicationData) || !proveStepEligible);

  // Direction-aware (skipStep, not advance): if the step is ever reached with Back it must not bounce the
  // applicant forward again. LoanAppForm hides this step from navigation once consent is written, so this
  // only runs on a direct mount, e.g. a wizard entered on this step with consent already present.
  useEffect(() => {
    if (!shouldSkip || advancedRef.current) return;
    advancedRef.current = true;
    skipStep();
  }, [shouldSkip, skipStep]);

  const visibility = useMemo(() => getProveStepFieldVisibility(applicationData), [applicationData]);
  const defaults = useMemo(() => getProveStepDefaults(applicationData), [applicationData]);
  const schema = useMemo(() => buildSchema(visibility), [visibility]);

  const showFallback = useCallback((error: string | undefined, source: string) => {
    track('Failed', { source });
    setPhase({ name: 'fallback', error });
  }, [track]);

  const start = useMutation<ProveStartResponse, ProveStepApiError, { body: ProveStartRequest; phoneNumber: string }>({
    mutationFn: ({ body }) => startProve(body),
    throwOnError: false,
    onSuccess: (session, { phoneNumber }) => {
      track('Started');
      // Held in component state only — see the module comment.
      setPhase({ name: 'verify', session, phoneNumber });
    },
    onError: (error) => showFallback(getProveFallbackError(error), 'ProveStartError'),
  });

  const onSubmit = (values: ProveStepFormValues) => {
    if (start.isPending || !applicationData) return;

    track('ContinueClicked');
    const phoneNumber = digitsOnly(values.phoneNumber);

    start.mutate({
      phoneNumber,
      body: {
        appId: applicationData.id,
        // Hidden fields are already on the application; send the stored value so the server can
        // write data.businessName in the same PATCH as the verified identity.
        businessName: (visibility.businessName ? values.businessName : defaults.businessName)?.trim() ?? '',
        dateOfBirth: formatToISODate(values.dateOfBirth),
        ...(visibility.email && values.email ? { email: values.email.trim() } : {}),
        flowType,
        phoneNumber,
        provePrefillConsent: new Date().toISOString(),
      },
    });
  };

  const onEnterManually = () => {
    track('EnterManuallyClicked');
    advance();
  };

  const onVerified = (response: ProveCompleteResponse) => {
    if (!applicationData) return;
    // The response carries the application with provePrefillConsent (+ the Prove person and business
    // name) written, so seed the cache the wizard reads: OWNER_STEP renders its review variant and
    // BUSINESS_SELECT fetches the M2 matches on its own, exactly as in M2. Nothing downstream changes.
    queryClient.setQueryData(['app', applicationData.id], response.application);
    track('Verified');
    track('ConsentRecorded');
    advance();
  };

  const onDeclined = () => {
    track('Declined');
    advance();
  };

  if (!loaded || shouldSkip) return <div className="mx-auto"><LoadingAnimation /></div>;

  if (phase.name === 'verify') {
    return (
      <ProveStepVerify
        appId={applicationData!.id}
        createAuthenticatorBuilder={createAuthenticatorBuilder}
        flowType={flowType}
        onFallback={(error, meta) => showFallback(error, meta.source)}
        onVerified={onVerified}
        phoneNumber={phase.phoneNumber}
        session={phase.session}
        trackAuth={trackAuth}
      />
    );
  }

  if (phase.name === 'fallback') {
    return (
      <Stack gap={6} style={{ maxWidth: '600px', margin: 'auto' }}>
        <FormattedMessageDiv className="h3 text-center" id="basicInfo.title" />
        <Card>
          <Stack gap={5}>
            <div>
              <FormattedMessageDiv className="h4 mb-2" id="proveStep.fallback.title" />
              <FormattedMessageDiv id="proveStep.fallback.description" />
            </div>
            <Button type="button" data-testid="proveStepFallbackContinue" onClick={onDeclined}>
              <FormattedMessageDiv id="proveStep.fallback.button" />
            </Button>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <FormWrapper<ProveStepFormValues> key="prove-step-form" schema={schema} defaultValues={defaults} onSubmit={onSubmit}>
      <Stack gap={6} style={{ maxWidth: '600px', margin: 'auto' }}>
        <FormattedMessageDiv className="h3 text-center" id="proveStep.pageTitle" />
        <Card>
          <Stack gap={5}>
            <div>
              <FormattedMessageDiv className="h4 mb-2" id="proveStep.title" />
              <FormattedMessageDiv className="mb-0" id="proveStep.description" />
            </div>
            {visibility.email && (
              <Row>
                <Col>
                  <FormInput name="email" data-testid="proveStepEmail" label={<FormattedMessageDiv id="proveStep.email" />} useYupErrors controlProps={{ inputMode: 'email' }} />
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                <FormInput
                  name="phoneNumber"
                  data-testid="proveStepPhone"
                  label={<FormattedMessageDiv id="proveStep.phone" />}
                  className="label-multiline"
                  helperText={
                    <FormattedMessageDiv
                      id="formInput.phoneNumberCell.helperText"
                      values={{
                        link: (txt: React.ReactNode) => (
                          <button type="button" className="btn btn-link p-0 fs-8 align-baseline" onClick={() => setShowHowWeVerify(true)}>{txt}</button>
                        ),
                      }}
                    />
                  }
                  formatValue={formatPhoneNumber}
                  controlProps={{ inputMode: 'tel' }}
                  useYupErrors
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput
                  name="dateOfBirth"
                  data-testid="proveStepDateOfBirth"
                  label={<FormattedMessageDiv id="formInput.dateOfBirth" />}
                  helperText={<FormattedMessageDiv id="formInput.dateOfBirth.helperText" />}
                  formatValue={formatDateInput}
                  useYupErrors
                  controlProps={{ inputMode: 'numeric' }}
                />
              </Col>
            </Row>
            {visibility.businessName && (
              <Row>
                <Col>
                  <FormInput name="businessName" data-testid="proveStepBusinessName" label={<FormattedMessageDiv id="proveStep.businessName" />} useYupErrors />
                </Col>
              </Row>
            )}
            <Button loading={start.isPending} disabled={start.isPending} type="submit" data-testid="proveStepContinue">
              <FormattedMessage id="proveStep.continue" />
            </Button>
            <div className="text-center">
              <button type="button" onClick={onEnterManually} className="btn btn-link fs-7 p-0" data-testid="proveStepEnterManually">
                <FormattedMessage id="proveStep.enterManually" />
              </button>
            </div>
          </Stack>
        </Card>
        <FormattedMessageDiv
          className="fs-8"
          id="basicInfo.disclaimer.submit"
          values={{
            terms: (txt: React.ReactNode) => <a target="_blank" rel="noreferrer" href="https://www.ondeck.com/terms">{txt}</a>,
            privacy: (txt: React.ReactNode) => <a target="_blank" rel="noreferrer" href="https://www.ondeck.com/privacy">{txt}</a>,
          }}
        />
      </Stack>
      <Modal
        showModal={showHowWeVerify}
        handleClose={() => setShowHowWeVerify(false)}
        title={<FormattedMessageDiv id="formInput.phoneNumberCell.modal.title" />}
        onButtonClick={() => setShowHowWeVerify(false)}
      >
        <FormattedMessageDiv
          id="formInput.phoneNumberCell.modal.body"
          values={{
            p1: (txt: React.ReactNode) => <p>{txt}</p>,
            p2: (txt: React.ReactNode) => <p>{txt}</p>,
            terms: (txt: React.ReactNode) => <a rel="noreferrer" href="https://www.ondeck.com/terms">{txt}</a>,
            privacy: (txt: React.ReactNode) => <a rel="noreferrer" href="https://www.ondeck.com/privacy">{txt}</a>,
          }}
        />
      </Modal>
    </FormWrapper>
  );
};

interface ProveStepVerifyProps {
  appId: string;
  createAuthenticatorBuilder?: ProveAuthenticatorBuilderFactory;
  flowType: ProveFlowType;
  onFallback: (error: string | undefined, meta: ProveFallbackMeta) => void;
  onVerified: (response: ProveCompleteResponse) => void;
  phoneNumber: string;
  session: ProveStartResponse;
  trackAuth: (action: ProveAuthAction, properties?: Record<string, unknown>, method?: ProveAuthMethod) => void;
}

// The in-step equivalent of the /login/mobile screen: the same SDK hook and the same presentational
// component, minus the email-OTP fallback (the applicant is already authed) and minus any routing.
const ProveStepVerify = ({ appId, createAuthenticatorBuilder, flowType, onFallback, onVerified, phoneNumber, session, trackAuth }: ProveStepVerifyProps) => {
  const fallbackStartedRef = useRef(false);
  const fallback = (error: string | undefined, meta: ProveFallbackMeta) => {
    if (fallbackStartedRef.current) return;
    fallbackStartedRef.current = true;
    authenticator.reset();
    onFallback(error, meta);
  };
  const { mutateAsync: submitComplete, isPending: verifying } = useMutation<ProveCompleteResponse, ProveStepApiError, void>({
    mutationFn: () => completeProve({ appId, key: session.key }),
    throwOnError: false,
    onError: (error) => {
      trackAuth('Submit', { result: 'Failure' });
      fallback(getProveFallbackError(error), { source: 'ProveCompleteError', httpStatus: error.status });
    },
    onSuccess: (response) => {
      trackAuth('Submit', { result: 'Success' });
      onVerified(response);
    },
  });
  const authenticator = useProveAuthenticator({
    authToken: session.authToken,
    createAuthenticatorBuilder,
    flowType,
    onFallback: fallback,
    onFinish: async () => {
      if (flowType !== 'mobile') trackAuth('Completed');
      await submitComplete();
    },
    phoneNumber,
    track: trackAuth,
  });
  const displayError = authenticator.resendThrottleErrorId ? <FormattedMessage id={authenticator.resendThrottleErrorId} /> : authenticator.sdkError;

  return (
    <ProveMobileAuthContent
      associate={false}
      displayError={displayError}
      flowType={flowType}
      instantLinkStatus={authenticator.instantLinkStatus}
      onContinue={noop}
      onContinueFallback={noop}
      onFallbackEmail={noop}
      onResendInstantLink={authenticator.resendInstantLink}
      onResendOtp={authenticator.resendOtp}
      onSubmitOtp={authenticator.submitOtp}
      otpError={authenticator.otpError}
      phoneMasked={session.phoneMasked}
      phoneNumber={phoneNumber}
      sent={authenticator.sent}
      showEmailFallback={false}
      showFallbackAction={false}
      verifying={verifying}
    />
  );
};

export default ProveStep;
